import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { 
  HttpRequest, 
  HttpHandler, 
  HttpEvent, 
  HttpInterceptor, 
  HttpErrorResponse 
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, finalize } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Track if we're already showing an auth error to prevent duplicates
  private isShowingAuthError = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth header for auth endpoints
    if (request.url.includes('sign-in') || request.url.includes('sign-up')) {
      return next.handle(request);
    }
    
    // Get the token from localStorage
    const token = localStorage.getItem('userToken');
    
    // Clone the request and add authorization header if token exists
    if (token) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Log the token being used (for debugging)
      console.log('Using token for request:', request.url);
    } else {
      console.warn('No token available for request:', request.url);
    }

    return next.handle(request).pipe(
      tap(event => {
        // Reset auth error flag on successful responses
        this.isShowingAuthError = false;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('HTTP error in interceptor:', error);
        
        // Handle 401 Unauthorized errors
        if (error.status === 401) {
          console.log('401 Unauthorized error detected');
          
          // Only show the error message once
          if (!this.isShowingAuthError) {
            this.isShowingAuthError = true;
            
            // Check if we're already on the login page to avoid redirect loops
            if (!window.location.href.includes('/auth/sign-in')) {
              this.snackBar.open('Session expired. Please login again.', 'Close', {
                duration: 3000,
                panelClass: ['error-snackbar']
              });
              
              // Store the current URL for redirection after login
              const currentUrl = this.router.url;
              if (currentUrl && !currentUrl.includes('/auth/')) {
                console.log('Storing redirect URL:', currentUrl);
                sessionStorage.setItem('redirectAfterLogin', currentUrl);
              }
              
              // Clear user data and redirect to login
              this.authService.logout(false); // Pass false to prevent automatic navigation
              this.router.navigate(['/auth/sign-in']);
            }
          }
        }
        // Handle other error statuses
        else if (error.status === 403) {
          this.snackBar.open('You do not have permission to access this resource.', 'Close', {
            duration: 3000,
            panelClass: ['error-snackbar']
          });
        }
        
        // Rethrow the error for further processing
        return throwError(() => error);
      }),
      finalize(() => {
        // Reset auth error flag after a short delay
        setTimeout(() => {
          this.isShowingAuthError = false;
        }, 1000);
      })
    );
  }
}
